// src/data/seatsOfRecord.js
//
// WO-001 S3 (2026-09-14, task list section 240). Design SS3: "the compiled roster in lcRoster.js
// becomes a projection too, or disappears - the picklist reads current terms from Firestore instead
// of from the bundle, and a change of officer stops requiring a deploy."
//
// This module PROJECTS the spine (offices/ + officeTerms/) into the roster shape every desk, picker
// and readiness count already consumes ({ scopeKey, craft, railroadKey, label }), so deskQueue.js,
// gcDesk.js, ClaimDetail.js and Readiness.js keep their logic and change only their source.
// src\data\lcRoster.js stays in the bundle as the FALLBACK for a committee whose seats are not yet
// seeded (offices empty) - and says so through `source: 'bundle'` - never as a second truth beside
// a seeded one. The label conventions are kept exactly: "(VACANT - reason)" is what isUnheldSeat()
// and Readiness.holderState() read; "(NOT OF RECORD)" is ruling 54's; a held seat prints the holder.
// Pure: the hook in src\hooks\useSeatsOfRecord.js does the reading.
import { officeStateOn } from './officeSuccession';

// Roster "Railroad" token -> app railroadKey (agreementData.js RAILROADS). Same map as
// scripts\gen-lc-roster.js; the seed writes railroadKey onto each office from it so the office
// carries the key and this map is only the fallback for an office seeded before it did.
export const RAILROAD_KEY = {
  'Amtrak': 'Amtrak / NRPC',
  'CSX': 'CSX Transportation — Former Conrail (Northern Lines)',
  'Conrail': 'Consolidated Rail Corporation (Conrail)',
  'D&H/CPR': 'Delaware & Hudson / Canadian Pacific',
};

export function seatLabel(office, terms, today) {
  const st = officeStateOn(office, terms, today);
  const who = st.state === 'held'
    ? (st.acting ? 'Acting: ' : '') + (st.term.holderName || 'holder on record')
    : st.state === 'vacant' ? 'VACANT - ' + (st.reason || 'no reason recorded')
    : 'NOT OF RECORD';
  return office.officeId + '  -  ' + (office.craft || '?') + ' | ' + (office.railroad || '?') + ' | ' + (office.territory || '?') + ' (' + who + ')';
}

// The roster as the spine holds it today. Local Chairperson seats only, sorted as the CSV roster is.
export function rosterFromSpine(offices, terms, today) {
  const d = today || new Date().toISOString().slice(0, 10);
  return (offices || [])
    .filter(o => o && o.type === 'local_committee')
    .map(o => {
      const st = officeStateOn(o, terms || [], d);
      return {
        scopeKey: o.officeId,
        craft: o.craft || '',
        railroadKey: o.railroadKey || RAILROAD_KEY[o.railroad] || o.railroad || '',
        label: seatLabel(o, terms || [], d),
        state: st.state,
        holderName: st.term ? st.term.holderName : null,
        holderUid: st.term ? st.term.holderUid : null,
        committee: o.parentOffice || null,
        source: 'spine',
      };
    });
}

// Which roster a screen uses: the spine when the committee's seats are seeded, the bundle otherwise -
// and the answer says which, so a screen can print it (VF-8: a projection names its source).
export function chooseRoster(spineRoster, bundleRoster) {
  if (Array.isArray(spineRoster) && spineRoster.length) return { roster: spineRoster, source: 'spine' };
  return { roster: (bundleRoster || []).map(r => ({ ...r, source: 'bundle' })), source: 'bundle' };
}
